import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SSI_ADC_INTERFACE_ID} from 'molbio/adcs/schema-configuration/AdcSchemaInterfaceConstants';
import {
  RegistrationTableRowData,
  SchemaInterfaceColumnTransformerBase,
} from 'notebook/integrations/registry/SchemaInterfaceColumnTransformerBase';
import {ImplementedStaticSchemaInterfaceGQL} from 'notebook/structured-tables/components/modals/RegistrationTableInsertModal';
import {
  ExistingBioentityReferenceStructuredTableColumn,
  ExistingBioentityStructuredTableColumn,
  StructuredTableColumn,
  StructuredTableColumnGroups,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';

export const ADC_PROTEIN_COLUMN_NAME = 'Protein';
export const ADC_LINKER_PAYLOAD_COLUMN_NAME = 'Linker-payload';

type AdcBioentityConfig = Pick<
  ExistingBioentityStructuredTableColumn,
  'bioentityTypeFilters' | 'schemaInterfaceId' | 'frontEndMetadata'
>;

/**
 * The picker config for each ADC entity-reference column. Only `columnGroup` is persisted, so this must be
 * re-derived and re-injected on every load (see `configureColumn`).
 */
function adcBioentityConfigForColumnGroup(
  columnGroup: StructuredTableColumn['columnGroup']
): AdcBioentityConfig | null {
  switch (columnGroup) {
    case StructuredTableColumnGroups.ADC_PROTEIN:
      // Antibodies are custom entities (BASIC_FOLDER_ITEM), not the PROTEIN model type, so scope the
      // picker by the antibody schema interface too.
      return {
        bioentityTypeFilters: [ModelTypeConstants.BASIC_FOLDER_ITEM],
        schemaInterfaceId: ANTIBODY_SCHEMA_INTERFACE_ID,
        frontEndMetadata: {validSchemas: 'ALL'},
      };
    case StructuredTableColumnGroups.ADC_LINKER_PAYLOAD:
      return {
        bioentityTypeFilters: [ModelTypeConstants.MOLECULE],
        frontEndMetadata: {validSchemas: 'ALL'},
      };
    default:
      return null;
  }
}

function isAdcInterface(implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL): boolean {
  return implementedSchemaInterface.schemaInterface.id === SSI_ADC_INTERFACE_ID;
}

function makeAdcBioentityColumn(
  name: string,
  columnGroup: StructuredTableColumnGroups
): StructuredTableColumn {
  // EXISTING_BIOENTITY_REFERENCE is a dedicated columnType so shared registration/resync/primary-sample
  // handling skips these by type, preserving the operator's selection rather than overwriting it with the
  // registered ADC. They render like EXISTING_BIOENTITY columns; columnGroup distinguishes Protein
  // (antibody) from Linker-payload (molecule). Picker config is injected on load, not here.
  //
  // TODO(BNCH-171149 follow-up): the selections are not yet wired into ADC creation -- the submit parser
  // matches only the ungrouped primary EXISTING_BIOENTITY column, so these values do not yet drive
  // conjugate creation. Wiring them in must add server-side validation that the Protein is an antibody and
  // the Linker-payload is a molecule; today only the frontend picker enforces this.
  return makeStructuredTableColumn({
    columnType: TableColumnType.EXISTING_BIOENTITY_REFERENCE,
    name,
    columnGroup,
    isRequired: true,
    isReadOnly: false,
  });
}

/**
 * Adds the ADC conjugate's input columns to a registration table for schemas implementing `ssi_adc`,
 * mirroring AntibodyRegTableColumnTransformer: a required Protein column scoped to antibodies and a
 * required Linker-payload column scoped to molecules.
 */
export default class AdcRegTableColumnTransformer implements SchemaInterfaceColumnTransformerBase {
  replaceExistingColumn(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    tableRowData: RegistrationTableRowData
  ): StructuredTableColumn | null {
    return null;
  }

  getNewColumns(
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    tableRowData: RegistrationTableRowData
  ): Array<StructuredTableColumn> {
    assert(isAdcInterface(implementedSchemaInterface), 'ADC transformer called with non-ADC interface');
    // Added regardless of whether the ADC is new or already exists. ADCs have no computed fields
    // surfacing these inputs, so (unlike antibodies) there is nothing for shouldHideColumn to hide.
    return [
      makeAdcBioentityColumn(ADC_PROTEIN_COLUMN_NAME, StructuredTableColumnGroups.ADC_PROTEIN),
      makeAdcBioentityColumn(ADC_LINKER_PAYLOAD_COLUMN_NAME, StructuredTableColumnGroups.ADC_LINKER_PAYLOAD),
    ];
  }

  shouldHideColumn(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): boolean {
    return false;
  }

  getFrontEndMetadata(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): unknown | null {
    return null;
  }

  /**
   * Re-injects the picker config onto an ADC column, keyed on its column group; returns null for non-ADC
   * columns. Runs on every load (including rehydration) since the config is not persisted. Implemented
   * (rather than getFrontEndMetadata) because ADC columns need top-level fields set, not just a
   * frontEndMetadata blob.
   */
  configureColumn(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): ExistingBioentityReferenceStructuredTableColumn | null {
    const config = adcBioentityConfigForColumnGroup(column.columnGroup);
    if (config == null) {
      return null;
    }
    assert(
      column.columnType === TableColumnType.EXISTING_BIOENTITY_REFERENCE,
      'ADC protein / linker-payload columns must be EXISTING_BIOENTITY_REFERENCE columns'
    );
    return {...column, ...config} as ExistingBioentityReferenceStructuredTableColumn;
  }
}
